import React, { useState, useEffect, useRef } from 'react';
import { MessageSquare, Calendar, User, ChevronRight, Loader2, AlertCircle, X, Trash2, ExternalLink } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { useNavigate } from 'react-router-dom';
import ChatHistoryService from '../../services/chatHistoryService';
import { getApiBaseUrl } from '../../utils/apiBaseUrl';
import '../../css/components/FullChat/LanguageSelector.css';

const AllChatsModal = ({ isOpen, onClose }) => {
  const { theme } = useTheme();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const modalRef = useRef(null);
  const [chats, setChats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedConversations, setExpandedConversations] = useState(new Set());

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      fetchAllChats();
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  const fetchAllChats = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('authToken');
      const headers = {
        'Content-Type': 'application/json',
      };

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${getApiBaseUrl()}/api/allchatsofuser`, {
        method: 'GET',
        headers: headers,
        credentials: 'include',
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => response.statusText);
        let errorMessage = `Failed to load chats: ${response.statusText}`;
        
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.error) {
            errorMessage = errorData.error;
          }
        } catch (e) {
          // Not JSON, use statusText
        }
        
        if (response.status === 401) {
          throw new Error(t('allChats.pleaseLogin'));
        } else if (response.status === 403) {
          throw new Error(errorMessage || t('allChats.accessForbidden'));
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      setChats(data);
    } catch (err) {
      console.error('Error fetching chats:', err);
      setError(err.message || 'Failed to load chats');
    } finally {
      setLoading(false);
    }
  };

  const toggleConversation = (conversationId) => {
    const newExpanded = new Set(expandedConversations);
    if (newExpanded.has(conversationId)) {
      newExpanded.delete(conversationId);
    } else {
      newExpanded.add(conversationId);
    }
    setExpandedConversations(newExpanded);
  };

  const handleDeleteConversation = async (e, conversationId) => {
    e.stopPropagation(); // Prevent triggering the conversation toggle
    
    if (!window.confirm(t('allChats.deleteConfirm'))) {
      return;
    }

    try {
      const success = await ChatHistoryService.deleteConversation(conversationId);
      if (success) {
        // Remove the conversation from the list
        if (chats && chats.conversations) {
          setChats({
            ...chats,
            conversations: chats.conversations.filter(conv => conv.conversation_id !== conversationId),
            total_conversations: chats.total_conversations - 1
          });
        }
        console.log('Conversation deleted successfully');
      } else {
        throw new Error('Failed to delete conversation');
      }
    } catch (error) {
      console.error('Error deleting conversation:', error);
      alert(t('allChats.failedToDelete'));
    }
  };

  const handleOpenConversation = (e, conversation) => {
    e.stopPropagation(); // Prevent triggering the conversation toggle
    
    // Navigate to chat page with conversation ID
    navigate(`/fullchat?conversationId=${conversation.conversation_id}`);
    onClose(); // Close the modal
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (!isOpen) return null;

  return (
    <div 
      className="language-selector-overlay" 
      style={{
        background: 'rgba(0, 0, 0, 0.7)',
        backdropFilter: 'blur(12px)',
        animation: 'fadeIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        zIndex: 2000
      }}
    >
      <div 
        ref={modalRef}
        className="language-selector-popup"
        style={{
          background: theme.modalBg || 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
          border: `1px solid ${theme.primaryBorder || 'rgba(59, 130, 246, 0.2)'}`,
          boxShadow: `0 25px 80px ${theme.primaryShadow || 'rgba(0, 0, 0, 0.4)'}, 0 8px 32px rgba(0, 0, 0, 0.3)`,
          animation: 'slideInUp 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          maxWidth: '900px',
          width: '90%',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Header */}
        <div 
          className="language-selector-header" 
          style={{
            borderBottom: `1px solid ${theme.primaryBorder || 'rgba(59, 130, 246, 0.2)'}`,
            background: theme.tertiaryBg || 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%)',
            padding: '24px 28px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{
              width: '48px',
              height: '48px',
              background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 24px rgba(59, 130, 246, 0.3)'
            }}>
              <MessageSquare size={24} style={{ color: 'white' }} />
            </div>
            <div>
              <h2 style={{
                fontSize: '24px',
                fontWeight: '700',
                color: theme.primaryText || 'white',
                margin: '0 0 4px 0',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
              }}>
                {t('allChats.myChats')}
              </h2>
              {chats && (
                <div style={{
                  display: 'flex',
                  gap: '16px',
                  fontSize: '14px',
                  color: theme.secondaryText || 'rgba(255, 255, 255, 0.7)'
                }}>
                  <span>{chats.total_conversations} {t('allChats.conversations')}</span>
                  <span>•</span>
                  <span>{chats.total_messages} {t('allChats.messages')}</span>
                </div>
              )}
            </div>
          </div>
          
          <button
            onClick={onClose}
            style={{
              width: '40px',
              height: '40px',
              background: 'rgba(239, 68, 68, 0.15)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              color: '#ef4444'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'rgba(239, 68, 68, 0.25)';
              e.target.style.transform = 'scale(1.05)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'rgba(239, 68, 68, 0.15)';
              e.target.style.transform = 'scale(1)';
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '24px 28px',
          background: theme.secondaryBg || 'rgba(15, 23, 42, 0.4)'
        }}>
          {loading ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '60px 20px',
              color: theme.secondaryText || 'rgba(255, 255, 255, 0.6)'
            }}>
              <Loader2 size={32} style={{ marginBottom: '20px', animation: 'spin 1s linear infinite' }} />
              <div style={{ fontSize: '16px' }}>{t('allChats.loadingChats')}</div>
            </div>
          ) : error ? (
            <div style={{
              textAlign: 'center',
              padding: '60px 20px',
              color: '#ef4444'
            }}>
              <AlertCircle size={48} style={{ marginBottom: '16px', opacity: 0.7 }} />
              <div style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
                {t('allChats.errorLoadingChats')}
              </div>
              <div style={{ fontSize: '14px', opacity: 0.8 }}>{error}</div>
              <button
                onClick={fetchAllChats}
                style={{
                  marginTop: '20px',
                  padding: '10px 20px',
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                {t('allChats.tryAgain')}
              </button>
            </div>
          ) : !chats || chats.conversations.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '60px 20px',
              color: theme.secondaryText || 'rgba(255, 255, 255, 0.6)'
            }}>
              <MessageSquare size={64} style={{ marginBottom: '20px', opacity: 0.5 }} />
              <div style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px', color: theme.primaryText || 'white' }}>
                {t('allChats.noChatsYet')}
              </div>
              <div style={{ fontSize: '14px', opacity: 0.7 }}>
                {t('allChats.startNewConversation')}
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {chats.conversations.map((conversation) => (
                <div
                  key={conversation.conversation_id}
                  style={{
                    background: theme.cardBg || 'rgba(30, 41, 59, 0.6)',
                    border: `1px solid ${theme.borderColor || 'rgba(59, 130, 246, 0.1)'}`,
                    borderRadius: '12px',
                    overflow: 'hidden',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <div
                    style={{
                      padding: '16px 20px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      transition: 'background 0.2s ease',
                      gap: '12px'
                    }}
                  >
                    <div
                      onClick={() => toggleConversation(conversation.conversation_id)}
                      style={{
                        flex: 1,
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        transition: 'background 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = theme.hoverBg || 'rgba(30, 41, 59, 0.8)';
                        e.currentTarget.style.borderRadius = '8px';
                        e.currentTarget.style.padding = '4px';
                        e.currentTarget.style.margin = '-4px';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.padding = '0';
                        e.currentTarget.style.margin = '0';
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{
                          fontSize: '16px',
                          fontWeight: '600',
                          color: theme.primaryText || 'white',
                          marginBottom: '8px'
                        }}>
                          {conversation.title || t('allChats.untitledConversation')}
                        </div>
                        <div style={{
                          display: 'flex',
                          gap: '16px',
                          fontSize: '13px',
                          color: theme.secondaryText || 'rgba(255, 255, 255, 0.7)',
                          alignItems: 'center'
                        }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <MessageSquare size={14} />
                            {conversation.message_count} {t('allChats.messagesCount')}
                          </span>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Calendar size={14} />
                            {formatDate(conversation.last_message_at)}
                          </span>
                          {conversation.business_type && (
                            <span style={{
                              padding: '2px 8px',
                              background: 'rgba(59, 130, 246, 0.2)',
                              color: '#3b82f6',
                              borderRadius: '6px',
                              fontSize: '12px'
                            }}>
                              {conversation.business_type}
                            </span>
                          )}
                        </div>
                      </div>
                      <ChevronRight
                        size={20}
                        style={{
                          color: theme.secondaryText || 'rgba(255, 255, 255, 0.7)',
                          transition: 'transform 0.2s ease',
                          transform: expandedConversations.has(conversation.conversation_id) ? 'rotate(90deg)' : 'rotate(0deg)',
                          marginLeft: '12px'
                        }}
                      />
                    </div>
                    
                    <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                      <button
                        onClick={(e) => handleOpenConversation(e, conversation)}
                        style={{
                          width: '36px',
                          height: '36px',
                          background: 'rgba(59, 130, 246, 0.15)',
                          border: '1px solid rgba(59, 130, 246, 0.3)',
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          color: '#3b82f6',
                          padding: 0
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'rgba(59, 130, 246, 0.25)';
                          e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.5)';
                          e.currentTarget.style.transform = 'scale(1.1)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'rgba(59, 130, 246, 0.15)';
                          e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.3)';
                          e.currentTarget.style.transform = 'scale(1)';
                        }}
                        title={t('allChats.openConversation')}
                      >
                        <ExternalLink size={16} />
                      </button>
                      
                      <button
                        onClick={(e) => handleDeleteConversation(e, conversation.conversation_id)}
                        style={{
                          width: '36px',
                          height: '36px',
                          background: 'rgba(239, 68, 68, 0.15)',
                          border: '1px solid rgba(239, 68, 68, 0.3)',
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          color: '#ef4444',
                          padding: 0
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'rgba(239, 68, 68, 0.25)';
                          e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.5)';
                          e.currentTarget.style.transform = 'scale(1.1)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'rgba(239, 68, 68, 0.15)';
                          e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.3)';
                          e.currentTarget.style.transform = 'scale(1)';
                        }}
                        title={t('allChats.deleteConversation')}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>

                  {expandedConversations.has(conversation.conversation_id) && (
                    <div style={{
                      borderTop: `1px solid ${theme.borderColor || 'rgba(59, 130, 246, 0.1)'}`,
                      padding: '16px 20px',
                      background: theme.tertiaryBg || 'rgba(15, 23, 42, 0.3)',
                      maxHeight: '400px',
                      overflowY: 'auto'
                    }}>
                      {conversation.messages && conversation.messages.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                          {conversation.messages.map((message, index) => (
                            <div
                              key={message.id || index}
                              style={{
                                background: theme.cardBg || 'rgba(30, 41, 59, 0.4)',
                                borderRadius: '8px',
                                padding: '12px',
                                borderLeft: `3px solid ${message.role === 'user' ? '#3b82f6' : '#10b981'}`
                              }}
                            >
                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '8px'
                              }}>
                                <span style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  fontSize: '13px',
                                  fontWeight: '600',
                                  color: theme.primaryText || 'white'
                                }}>
                                  {message.role === 'user' ? (
                                    <User size={14} />
                                  ) : (
                                    <MessageSquare size={14} />
                                  )}
                                  {message.role === 'user' ? t('allChats.you') : t('allChats.assistant')}
                                </span>
                                <span style={{
                                  fontSize: '12px',
                                  color: theme.secondaryText || 'rgba(255, 255, 255, 0.6)'
                                }}>
                                  {formatDate(message.created_at)}
                                </span>
                              </div>
                              <div style={{
                                color: theme.primaryText || 'white',
                                fontSize: '14px',
                                lineHeight: '1.6',
                                whiteSpace: 'pre-wrap',
                                wordWrap: 'break-word'
                              }}>
                                {message.content}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div style={{
                          textAlign: 'center',
                          padding: '20px',
                          color: theme.secondaryText || 'rgba(255, 255, 255, 0.6)'
                        }}>
                          {t('allChats.noMessagesInConversation')}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AllChatsModal;


